import { useEffect, useState } from 'react';

import SettingsModal from '../components/SettingsModal';
import TopBar from '../components/TopBar';
import { useStore } from '../store';

export default function CardPlayScreen() {
  const s = useStore();
  const [showSettings, setShowSettings] = useState(false);
  const lesson = s.currentLesson;
  const item = s.currentItem;

  // Stop speaking when leaving the screen.
  useEffect(() => () => s.stopPlayback(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard control — a web affordance the touch builds had no need for.
  useEffect(() => {
    if (showSettings) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA)$/.test(el.tagName)) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          s.prev(true);
          break;
        case 'ArrowRight':
          e.preventDefault();
          s.next(false);
          break;
        case ' ':
          e.preventDefault();
          s.togglePlay();
          break;
        case 'Enter':
          e.preventDefault();
          s.flipCard();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [s, showSettings]);

  if (!lesson || !item) return null;

  const total = lesson.items.length;
  const skipped = s.isItemSkipped(s.currentIndex);

  const frontText = s.progress.show_word ? item.eng_text : s.t('card.tap_show_english');
  const backText = s.progress.show_meaning ? item.kor_text || '—' : s.t('card.tap_show_meaning');

  return (
    <div className="screen">
      <TopBar
        title={s.t('mode.card_play.title')}
        subtitle={lesson.unit.trim()}
        onBack={s.backToScenarioMenu}
        onSettings={() => setShowSettings(true)}
        backLabel={s.t('nav.back')}
        settingsLabel={s.t('screen.settings.title')}
      />

      <div className="card-body">
        <div className="card-head">
          <button
            type="button"
            className="check-row"
            onClick={() => s.toggleItemSkipped(s.currentIndex)}
            role="switch"
            aria-checked={skipped}
          >
            <span className={`checkbox checkbox--muted${skipped ? ' is-on' : ''}`} aria-hidden="true">
              ✓
            </span>
            <span className="check-row__label">{s.t('card.skip_checkbox')}</span>
          </button>
          <span className="counter">
            {s.currentIndex + 1} / {total}
          </span>
        </div>

        <button type="button" className="flashcard" onClick={s.flipCard}>
          {!!item.pack && <span className="flashcard__pack">{item.pack}</span>}
          <p className={`flashcard__text${s.cardShowingFront ? '' : ' flashcard__text--back'}`}>
            {s.cardShowingFront ? frontText : backText}
          </p>
          <p className="flashcard__hint">
            {s.cardShowingFront ? s.t('card.tap_hint_meaning') : s.t('card.tap_hint_english')}
          </p>
        </button>

        <div className="speaking" aria-live="polite">
          {s.isSpeaking ? s.t('card.speaking') : ''}
        </div>

        <div className="controls">
          <button
            type="button"
            className="circle-btn"
            disabled={s.currentIndex === 0}
            onClick={() => s.prev(true)}
            aria-label={s.t('nav.prev')}
          >
            ◀
          </button>
          <button
            type="button"
            className="play-btn"
            onClick={s.togglePlay}
            aria-label={s.isSpeaking ? s.t('card.stop_button') : s.t('card.play_button')}
          >
            {s.isSpeaking ? '■' : '▶'}
          </button>
          <button
            type="button"
            className="circle-btn"
            disabled={s.currentIndex >= total - 1}
            onClick={() => s.next(false)}
            aria-label={s.t('nav.next')}
          >
            ▶
          </button>
        </div>
      </div>

      <SettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
}
